import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { theme } from 'assets/styles/theme';
import { CreateButton } from 'components/buttons/CreateButton';
import CampaignTile from 'components/campaign/campaign-tile/CampaignTile';
import { LoadingContainer } from 'components/containers/Containers';
import { LoadingIndicator } from 'components/ui-elements/LoadingIndicator';
import { useCampaignPopup } from 'features/add-campaign/form/useCampaignPopup';
import { useResponsive } from 'hooks/useResponsive';
import useMyCampaigns from 'services/campaign/useMyCampaigns';

import { MyActivitySectionTitle } from './MyActivitySectionTitle';
import { MyActivitySeeAllButton } from './MyActivitySeeAllButton';
import { NoContent } from './NoContent';

const Container = styled.div`
  margin-top: 48px;

  ${theme.mq.phone} {
    margin-top: 24px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;

  ${theme.mq.tablet} {
    gap: 12px;
  }

  ${theme.mq.phone} {
    gap: 8px;
    margin-bottom: 12px;
  }
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;

  ${theme.mq.phone} {
    gap: 8px;
  }
`;

const TilesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  width: 100%;
  gap: 24px;

  ${theme.mq.tablet} {
    gap: 12px;
  }
`;

const CAMPAIGNS_PREVIEW_SIZE = 2;

export const CampaignsSection = () => {
  const { t } = useTranslation('myProfile');
  const { state } = useMyCampaigns({ pageSize: CAMPAIGNS_PREVIEW_SIZE });
  const showSeeAll = !state.loading && state.hasNextPage;
  const { showAddCampaignPopup } = useCampaignPopup();
  const onCreateClick = () => showAddCampaignPopup();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  const renderCampaigns = () => (
    <TilesContainer>
      {state.campaigns.map(campaign => (
        <CampaignTile key={campaign.id} campaign={campaign} onClick={() => navigate(`/campaigns/${campaign.id}`)} />
      ))}
    </TilesContainer>
  );

  const renderNoContent = () => (
    <NoContent
      text={t('my-activity.no-campaigns')}
      buttonText={t('my-activity.create-first')}
      onClick={onCreateClick}
    />
  );

  const resolveButtonText = () => (isMobile ? t('my-activity.create') : t('my-activity.create-campaign'));

  const renderLoading = () => (
    <LoadingContainer>
      <LoadingIndicator />
    </LoadingContainer>
  );

  const renderContent = () => {
    if (state.loading) return renderLoading();
    if (state.campaigns && state.campaigns.length > 0) return renderCampaigns();
    return renderNoContent();
  };

  const handleSeeAll = () => navigate('/my-profile/my-activity/campaigns');

  return (
    <Container data-testid={'campaigns-section'}>
      <HeaderContainer>
        <MyActivitySectionTitle>{t('my-activity.campaigns')}</MyActivitySectionTitle>
        <RightControls>
          <CreateButton onClick={onCreateClick}>{resolveButtonText()}</CreateButton>
          {showSeeAll && (
            <MyActivitySeeAllButton onClick={handleSeeAll}>{t('my-activity.see-all')}</MyActivitySeeAllButton>
          )}
        </RightControls>
      </HeaderContainer>
      {renderContent()}
    </Container>
  );
};
